import { Injectable } from '@angular/core';
import { Observable, of, NEVER } from 'rxjs';
import { IPatch } from '@nimbus-library/features/shared/models/interfaces';
import { TeamsContextResolver } from '../common/context-resolvers/teams-context.resolver';
import { IMicrosoftTeamsContextAccessor } from '../contract/i-microsoft-teams-context.accessor';
import { AllClientTypes, ClientType } from '../models/client-type';
import { IUserLoginInfo } from '../models/i-user-login-info';
import { ITeamsContext } from '../models/i-teams.context';

@Injectable()
export class BrowserMicrosoftTeamsContextAccessor implements IMicrosoftTeamsContextAccessor {
  private readonly _context: ITeamsContext;

  public constructor(private readonly teamsContextResolver: TeamsContextResolver) {
    this._context = teamsContextResolver.context;
  }

  public get snapshot() {
    return this._context;
  }

  public getContext$(): Observable<ITeamsContext> {
    return this._context ? of(this._context) : NEVER;
  }

  public getClientInfo$(): Observable<AllClientTypes> {
    return of(ClientType.Browser);
  }

  public getLoginHint$(): Observable<string> {
    return of(this._context ? this._context.loginHint : null);
  }

  public getThemeInformation$(): Observable<string> {
    return of('default');
  }

  public isStandAlone(): boolean {
    return true;
  }

  public isStaticTab(): boolean {
    return false;
  }

  public refreshContext(): void {
    this.teamsContextResolver.refreshContext();
  }

  public trySpecifyUser(user: IUserLoginInfo): ITeamsContext {
    if (!user) {
      return null;
    }

    const teamsContext: IPatch<ITeamsContext> = {
      userObjectId: user.userId,
      tenantId: user.tenantId,
      loginHint: user.loginHint,
    };

    // When the signed-in user changes (e.g. account switch), the previously selected team
    // belonged to the old user and may no longer be accessible. Drop the team-scoped context
    // so we don't end up checking permissions against a team the new user cannot see.
    const isDifferentUser = !!this._context.userObjectId && this._context.userObjectId !== user.userId;
    if (isDifferentUser) {
      teamsContext.groupId = null;
      teamsContext.teamId = null;
      teamsContext.teamName = null;
      teamsContext.conversationId = null;
      teamsContext.subEntityId = null;
    }

    Object.assign(this._context, teamsContext);

    this.teamsContextResolver.updateContext(this._context);

    return this._context;
  }
}
